/**
 * App - Root application component
 *
 * Main entry point for the Eye On You e-commerce platform. Sets up the application
 * with necessary providers for routing, state management, SEO, and UI features.
 *
 * @returns {JSX.Element} The configured application component tree
 */

// React
import { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

// Context providers
import { MenuProvider } from "./contexts/MenuContext";
import { FooterProvider } from "./contexts/FooterContext";
import { CookieProvider } from "./contexts/CookieContext";
import { BrandingProvider } from "./contexts/BrandingContext";

// Components
import { CookieBanner } from "./components/content";
import SEO from "./components/layout/seo";

// Utils
import ScrollToTop from "./utils/scrollToTop";

// Hooks
import useGoogleAnalytics from "./hooks/useGoogleAnalytics";
import useMetaPixel from "./hooks/useMetaPixel";

// Pages
const Home = lazy(() => import("./pages/home/home.jsx"));
const Product = lazy(() => import("./pages/product/Product"));
const FramesPage = lazy(() => import("./pages/eyewear/FramesPage.jsx"));
const FrameCollectionPage = lazy(() => import("./pages/eyewear/FrameCollectionPage.jsx"));
const SunglassesPage = lazy(() => import("./pages/eyewear/SunglassesPage.jsx"));
const AccessoriesPage = lazy(() => import("./pages/eyewear/AccessoriesPage.jsx"));
const BlogPage = lazy(() => import("./pages/blog/BlogPage.jsx"));
const BlogPost = lazy(() => import("./pages/blog-post/BlogPost.jsx"));
const About = lazy(() => import("./pages/other/About.jsx"));
const Contact = lazy(() => import("./pages/other/Contact.jsx"));
const Faq = lazy(() => import("./pages/other/Faq.jsx"));
const PrivacyPolicy = lazy(() => import("./pages/other/PrivacyPolicy.jsx"));
const TermsAndConditions = lazy(() => import("./pages/other/TermsAndConditions.jsx"));
const Cart = lazy(() => import("./pages/other/Cart.jsx"));
const NotFound = lazy(() => import("./pages/other/NotFound.jsx"));
const ComingSoon = lazy(() => import("./pages/other/ComingSoon.jsx"));

/**
 * Analytics wrapper component
 * Calls useGoogleAnalytics hook inside Router context
 */
const AnalyticsWrapper = () => {
  useGoogleAnalytics();
  useMetaPixel();
  return null;
};

/**
 * Hide cookie banner on specific routes like the coming soon page.
 */
const CookieBannerGuard = () => {
  const location = useLocation();
  const publicUrl = process.env.PUBLIC_URL || "";
  const comingSoonPath = `${publicUrl}/coming-soon`;

  if (location.pathname === comingSoonPath) {
    return null;
  }

  return <CookieBanner />;
};

const App = () => {
  return (
    <HelmetProvider>
      <BrandingProvider>
        <SEO title="Eye On You" />
        <CookieProvider>
          <MenuProvider>
            <FooterProvider>
              <Router>
                <AnalyticsWrapper />
                <ScrollToTop>
                  <Suspense
                    fallback={
                      <div className="preloader-wrapper">
                        <div className="preloader">
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    }
                  >
                    <Routes>
                      {/* Home */}
                      <Route path={process.env.PUBLIC_URL + "/"} element={<Home />} />

                      {/* Product pages */}
                      <Route path={process.env.PUBLIC_URL + "/frames"} element={<FramesPage />} />
                      <Route
                        path={process.env.PUBLIC_URL + "/frames/:collectionSlug"}
                        element={<FrameCollectionPage />}
                      />
                      <Route path={process.env.PUBLIC_URL + "/sunglasses"} element={<SunglassesPage />} />
                      <Route path={process.env.PUBLIC_URL + "/accessories"} element={<AccessoriesPage />} />

                      {/* Individual product pages */}
                      <Route path={process.env.PUBLIC_URL + "/product/:type/:slug"} element={<Product />} />
                      <Route path={process.env.PUBLIC_URL + "/product/:slug"} element={<Product />} />

                      {/* Blog pages */}
                      <Route path={process.env.PUBLIC_URL + "/blog"} element={<BlogPage />} />
                      <Route path={process.env.PUBLIC_URL + "/blog/:slug"} element={<BlogPost />} />

                      {/* Informational pages */}
                      <Route path={process.env.PUBLIC_URL + "/about"} element={<About />} />
                      <Route path={process.env.PUBLIC_URL + "/contact"} element={<Contact />} />
                      <Route path={process.env.PUBLIC_URL + "/faq"} element={<Faq />} />
                      <Route path={process.env.PUBLIC_URL + "/privacy-policy"} element={<PrivacyPolicy />} />
                      <Route path={process.env.PUBLIC_URL + "/terms-and-conditions"} element={<TermsAndConditions />} />

                      {/* Cart */}
                      <Route path={process.env.PUBLIC_URL + "/cart"} element={<Cart />} />

                      {/* Coming soon */}
                      <Route path={process.env.PUBLIC_URL + "/coming-soon"} element={<ComingSoon />} />

                      {/* 404 Not Found */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                  <CookieBannerGuard />
                </ScrollToTop>
              </Router>
            </FooterProvider>
          </MenuProvider>
        </CookieProvider>
      </BrandingProvider>
    </HelmetProvider>
  );
};

export default App;
